/**
 * <Page> — top-level page wrapper.
 *
 * Sets the canonical page background, height, and content max-width. Pair
 * with <PageHeader> at the top, <Toolbar> for filter/action regions, and
 * <Section> / <Card> for body content.
 *
 * Variants:
 *   - `width="default"` — bounded max-width (1280px) for documentation/forms
 *   - `width="full"`     — full-bleed for operational dashboards (default)
 *
 * Spec: docs/design-system/03-spacing-layout.md
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../utils/cn';

export interface PageProps extends HTMLAttributes<HTMLDivElement> {
  width?: 'default' | 'full' | 'narrow';
  /** Outer-most page padding. Default `md`. */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  children: ReactNode;
}

const WIDTH_CLASSES: Record<NonNullable<PageProps['width']>, string> = {
  full: 'w-full',
  default: 'w-full max-w-[1280px] mx-auto',
  narrow: 'w-full max-w-[820px] mx-auto',
};

const PADDING_CLASSES: Record<NonNullable<PageProps['padding']>, string> = {
  none: '',
  sm: 'px-4 py-3',
  md: 'px-6 py-4',
  lg: 'px-8 py-6',
};

export const Page = forwardRef<HTMLDivElement, PageProps>(function Page(
  { width = 'full', padding = 'md', className, children, ...rest },
  ref,
) {
  return (
    <div ref={ref} className={cn('h-full flex flex-col bg-bg text-text', className)} {...rest}>
      <div className={cn(WIDTH_CLASSES[width], PADDING_CLASSES[padding], 'flex flex-col gap-4 flex-1 min-h-0')}>
        {children}
      </div>
    </div>
  );
});

Page.displayName = 'Page';

export default Page;
